/**
 * EDS Component Library for MCP UI Renderer
 * Maps remote-dom elements to shadcn/EDS components
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Component wrappers for remote-dom
const EDSButton: React.FC<any> = ({ label, variant, size, onClick, children, ...props }) => (
  <Button variant={variant as any} size={size as any} onClick={onClick} {...props}>
    {label || children}
  </Button>
);

const EDSCard: React.FC<any> = ({ children, ...props }) => (
  <Card {...props}>{children}</Card>
);

const EDSCardHeader: React.FC<any> = ({ children, ...props }) => (
  <CardHeader {...props}>{children}</CardHeader>
);

const EDSCardTitle: React.FC<any> = ({ title, children, ...props }) => (
  <CardTitle {...props}>{title || children}</CardTitle>
);

const EDSCardDescription: React.FC<any> = ({ description, children, ...props }) => (
  <CardDescription {...props}>{description || children}</CardDescription>
);

const EDSCardContent: React.FC<any> = ({ children, ...props }) => (
  <CardContent {...props}>{children}</CardContent>
);

const EDSCardFooter: React.FC<any> = ({ children, ...props }) => (
  <CardFooter {...props}>{children}</CardFooter>
);

const EDSBadge: React.FC<any> = ({ label, variant, children, ...props }) => (
  <Badge variant={variant as any} {...props}>
    {label || children}
  </Badge>
);

const EDSAlert: React.FC<any> = ({ variant, children, ...props }) => (
  <Alert variant={variant as any} {...props}>
    {children}
  </Alert>
);

const EDSAlertTitle: React.FC<any> = ({ title, children, ...props }) => (
  <AlertTitle {...props}>{title || children}</AlertTitle>
);

const EDSAlertDescription: React.FC<any> = ({ description, children, ...props }) => (
  <AlertDescription {...props}>{description || children}</AlertDescription>
);

const EDSInput: React.FC<any> = ({ value, placeholder, onChange, ...props }) => (
  <Input value={value} placeholder={placeholder} onChange={onChange} {...props} />
);

const EDSLabel: React.FC<any> = ({ text, children, ...props }) => (
  <Label {...props}>{text || children}</Label>
);

const EDSSeparator: React.FC<any> = ({ orientation, ...props }) => (
  <Separator orientation={orientation as any} {...props} />
);

const EDSProgress: React.FC<any> = ({ value, ...props }) => (
  <Progress value={value} {...props} />
);

const EDSSkeleton: React.FC<any> = ({ className, ...props }) => (
  <Skeleton className={className} {...props} />
);

const EDSTable: React.FC<any> = ({ children, ...props }) => (
  <Table {...props}>{children}</Table>
);

const EDSTableHeader: React.FC<any> = ({ children, ...props }) => (
  <TableHeader {...props}>{children}</TableHeader>
);

const EDSTableBody: React.FC<any> = ({ children, ...props }) => (
  <TableBody {...props}>{children}</TableBody>
);

const EDSTableRow: React.FC<any> = ({ children, ...props }) => (
  <TableRow {...props}>{children}</TableRow>
);

const EDSTableHead: React.FC<any> = ({ text, children, ...props }) => (
  <TableHead {...props}>{text || children}</TableHead>
);

const EDSTableCell: React.FC<any> = ({ text, children, ...props }) => (
  <TableCell {...props}>{text || children}</TableCell>
);

const EDSTabs: React.FC<any> = ({ defaultValue, children, ...props }) => (
  <Tabs defaultValue={defaultValue} {...props}>
    {children}
  </Tabs>
);

const EDSTabsList: React.FC<any> = ({ children, ...props }) => (
  <TabsList {...props}>{children}</TabsList>
);

const EDSTabsTrigger: React.FC<any> = ({ value, label, children, ...props }) => (
  <TabsTrigger value={value} {...props}>
    {label || children}
  </TabsTrigger>
);

const EDSTabsContent: React.FC<any> = ({ value, children, ...props }) => (
  <TabsContent value={value} {...props}>
    {children}
  </TabsContent>
);

// Basic HTML elements with EDS styling
const EDSDiv: React.FC<any> = ({ children, className, ...props }) => (
  <div className={className} {...props}>
    {children}
  </div>
);

const EDSText: React.FC<any> = ({ text, children, className, ...props }) => (
  <p className={className} {...props}>
    {text || children}
  </p>
);

const EDSHeading: React.FC<any> = ({ level = 2, text, children, className, ...props }) => {
  const Tag = `h${level}` as keyof JSX.IntrinsicElements;
  return (
    <Tag className={className} {...props}>
      {text || children}
    </Tag>
  );
};

/**
 * EDS Component Library for remote-dom
 */
export const edsComponentLibrary = {
  name: 'eds-library',
  elements: [
    // Button
    {
      tagName: 'eds-button',
      component: EDSButton,
      propMapping: {
        label: 'label',
        variant: 'variant',
        size: 'size',
      },
      eventMapping: {
        click: 'onClick',
      },
    },
    // Card components
    {
      tagName: 'eds-card',
      component: EDSCard,
      propMapping: {},
    },
    {
      tagName: 'eds-card-header',
      component: EDSCardHeader,
      propMapping: {},
    },
    {
      tagName: 'eds-card-title',
      component: EDSCardTitle,
      propMapping: {
        title: 'title',
      },
    },
    {
      tagName: 'eds-card-description',
      component: EDSCardDescription,
      propMapping: {
        description: 'description',
      },
    },
    {
      tagName: 'eds-card-content',
      component: EDSCardContent,
      propMapping: {},
    },
    {
      tagName: 'eds-card-footer',
      component: EDSCardFooter,
      propMapping: {},
    },
    // Badge
    {
      tagName: 'eds-badge',
      component: EDSBadge,
      propMapping: {
        label: 'label',
        variant: 'variant',
      },
    },
    // Alert
    {
      tagName: 'eds-alert',
      component: EDSAlert,
      propMapping: {
        variant: 'variant',
      },
    },
    {
      tagName: 'eds-alert-title',
      component: EDSAlertTitle,
      propMapping: {
        title: 'title',
      },
    },
    {
      tagName: 'eds-alert-description',
      component: EDSAlertDescription,
      propMapping: {
        description: 'description',
      },
    },
    // Input & Label
    {
      tagName: 'eds-input',
      component: EDSInput,
      propMapping: {
        value: 'value',
        placeholder: 'placeholder',
      },
      eventMapping: {
        change: 'onChange',
      },
    },
    {
      tagName: 'eds-label',
      component: EDSLabel,
      propMapping: {
        text: 'text',
      },
    },
    // Separator
    {
      tagName: 'eds-separator',
      component: EDSSeparator,
      propMapping: {
        orientation: 'orientation',
      },
    },
    // Progress
    {
      tagName: 'eds-progress',
      component: EDSProgress,
      propMapping: {
        value: 'value',
      },
    },
    // Skeleton
    {
      tagName: 'eds-skeleton',
      component: EDSSkeleton,
      propMapping: {
        className: 'className',
      },
    },
    // Table components
    {
      tagName: 'eds-table',
      component: EDSTable,
      propMapping: {},
    },
    {
      tagName: 'eds-table-header',
      component: EDSTableHeader,
      propMapping: {},
    },
    {
      tagName: 'eds-table-body',
      component: EDSTableBody,
      propMapping: {},
    },
    {
      tagName: 'eds-table-row',
      component: EDSTableRow,
      propMapping: {},
    },
    {
      tagName: 'eds-table-head',
      component: EDSTableHead,
      propMapping: {
        text: 'text',
      },
    },
    {
      tagName: 'eds-table-cell',
      component: EDSTableCell,
      propMapping: {
        text: 'text',
      },
    },
    // Tabs
    {
      tagName: 'eds-tabs',
      component: EDSTabs,
      propMapping: {
        defaultValue: 'defaultValue',
      },
    },
    {
      tagName: 'eds-tabs-list',
      component: EDSTabsList,
      propMapping: {},
    },
    {
      tagName: 'eds-tabs-trigger',
      component: EDSTabsTrigger,
      propMapping: {
        value: 'value',
        label: 'label',
      },
    },
    {
      tagName: 'eds-tabs-content',
      component: EDSTabsContent,
      propMapping: {
        value: 'value',
      },
    },
    // Basic HTML elements
    {
      tagName: 'eds-div',
      component: EDSDiv,
      propMapping: {
        className: 'className',
      },
    },
    {
      tagName: 'eds-text',
      component: EDSText,
      propMapping: {
        text: 'text',
        className: 'className',
      },
    },
    {
      tagName: 'eds-heading',
      component: EDSHeading,
      propMapping: {
        level: 'level',
        text: 'text',
        className: 'className',
      },
    },
  ],
};

/**
 * Remote element configurations for EDS components
 */
export const edsRemoteElements = [
  // Button
  {
    tagName: 'eds-button',
    properties: {
      label: { type: String },
      variant: { type: String }, // default, destructive, outline, secondary, ghost, link
      size: { type: String }, // default, sm, lg, icon
    },
    events: {
      click: {},
    },
  },
  // Card
  {
    tagName: 'eds-card',
    properties: {},
  },
  {
    tagName: 'eds-card-header',
    properties: {},
  },
  {
    tagName: 'eds-card-title',
    properties: {
      title: { type: String },
    },
  },
  {
    tagName: 'eds-card-description',
    properties: {
      description: { type: String },
    },
  },
  {
    tagName: 'eds-card-content',
    properties: {},
  },
  {
    tagName: 'eds-card-footer',
    properties: {},
  },
  // Badge
  {
    tagName: 'eds-badge',
    properties: {
      label: { type: String },
      variant: { type: String }, // default, secondary, destructive, outline
    },
  },
  // Alert
  {
    tagName: 'eds-alert',
    properties: {
      variant: { type: String }, // default, destructive
    },
  },
  {
    tagName: 'eds-alert-title',
    properties: {
      title: { type: String },
    },
  },
  {
    tagName: 'eds-alert-description',
    properties: {
      description: { type: String },
    },
  },
  // Input & Label
  {
    tagName: 'eds-input',
    properties: {
      value: { type: String },
      placeholder: { type: String },
    },
    events: {
      change: {},
    },
  },
  {
    tagName: 'eds-label',
    properties: {
      text: { type: String },
    },
  },
  // Separator
  {
    tagName: 'eds-separator',
    properties: {
      orientation: { type: String }, // horizontal, vertical
    },
  },
  // Progress
  {
    tagName: 'eds-progress',
    properties: {
      value: { type: Number },
    },
  },
  // Skeleton
  {
    tagName: 'eds-skeleton',
    properties: {
      className: { type: String },
    },
  },
  // Table
  {
    tagName: 'eds-table',
    properties: {},
  },
  {
    tagName: 'eds-table-header',
    properties: {},
  },
  {
    tagName: 'eds-table-body',
    properties: {},
  },
  {
    tagName: 'eds-table-row',
    properties: {},
  },
  {
    tagName: 'eds-table-head',
    properties: {
      text: { type: String },
    },
  },
  {
    tagName: 'eds-table-cell',
    properties: {
      text: { type: String },
    },
  },
  // Tabs
  {
    tagName: 'eds-tabs',
    properties: {
      defaultValue: { type: String },
    },
  },
  {
    tagName: 'eds-tabs-list',
    properties: {},
  },
  {
    tagName: 'eds-tabs-trigger',
    properties: {
      value: { type: String },
      label: { type: String },
    },
  },
  {
    tagName: 'eds-tabs-content',
    properties: {
      value: { type: String },
    },
  },
  // Basic elements
  {
    tagName: 'eds-div',
    properties: {
      className: { type: String },
    },
  },
  {
    tagName: 'eds-text',
    properties: {
      text: { type: String },
      className: { type: String },
    },
  },
  {
    tagName: 'eds-heading',
    properties: {
      level: { type: Number },
      text: { type: String },
      className: { type: String },
    },
  },
];
